import { useEffect, useReducer } from 'react'

function getLink(style: HTMLLinkElement['href']) {
  return document.querySelector<HTMLLinkElement>(`link[href="${style}"]`)
}

async function loadStyle(style: HTMLLinkElement['href']) {
  return new Promise((resolve, reject) => {
    let link = getLink(style)
    if (link) {
      resolve(link)
      return
    }

    link = document.createElement('link')
    link.href = style
    link.onload = () => resolve(style)
    link.onerror = reject
    link.rel = 'stylesheet'

    document.head.appendChild(link)
  })
}

async function loadStyles(styles: Array<HTMLLinkElement['href']>) {
  return Promise.all(styles.map(loadStyle))
}

interface StyleState {
  isLoading: boolean
  hasError: boolean
}

const initialState = {
  isLoading: true,
  hasError: false,
}

type StyleAction = 'loaded' | 'failed'

function reducer(state: StyleState, action: StyleAction) {
  switch (action) {
    case 'loaded':
      return {
        isLoading: false,
        hasError: false,
      }
    case 'failed':
      return {
        isLoading: false,
        hasError: true,
      }
    default:
      return state
  }
}

function useExternalStyles(styles: Array<HTMLLinkElement['href']>) {
  const [{ isLoading, hasError }, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    async function load() {
      try {
        await loadStyles(styles)
        dispatch('loaded')
      } catch (error) {
        dispatch('failed')
      }
    }

    load()
  }, [styles])

  return { isLoading, hasError }
}

export { useExternalStyles }
